import { z } from 'zod';
import { CHECKOUT_VALIDATION_ERROR_CODES, CHECKOUT_ERROR_SECTIONS, CHECKOUT_PAGES, CREDIT_APP_FIELD_IDS, SUPPORT_TOPICS } from '../../constants.js';
import { DiscountTypesSchema } from '../../types/DiscountTypes.js';
import { OnlineSaleStatus } from '../../types/inventory/OnlineSale.js';
import { nudgeOrderingStrategySchema, nudgeActionKindSchema, nudgeAssetKindSchema, nudgePageSchema } from '../../types/nudges/Nudge.js';
import { SDK_AVAILABILITY_IDENTIFIER_TYPES, SDK_AVAILABILITY_REPORTED_REASONS } from '../../types/sdk/SdkCheckout.js';
import { EVENT_DESTINATION, DEALER_DATALAYER_DESTINATIONS, CLIENT_AD_EVENT_DESTINATIONS } from './destinations.js';
import { ADDRESS_FIELD, ADDRESS_SUBFIELD, CREDIT_APP_SECTION, DELIVERY_OPTION, CHECKOUT_PAYMENT_OPTION, PREORDER_PAYMENT_OPTION, TAR_OPTION, PREQUAL_OUTCOME } from './shared-fields.js';

/**
 * Engagement events—product interactions that aren't funnel steps: listing
 * views, searches, filter usage, modal opens, link clicks, configurator
 * interactions, chat/contact clicks. Fired client-side via the Carthage,
 * Checkout, Buyer Portal, sales-agent, and Ekho SDK wrappers.
 *
 * `canonical` is the internal Ekho-standard name for each event. `wireName`,
 * where present, is the historic emitted string for events not yet switched
 * over to their canonical; the wire emits `wireName ?? canonical` (see
 * `getEventName`), so an event carrying both emits under its `wireName` with
 * zero data discontinuity.
 */
/**
 * A blocked order-placement attempt, shared by the checkout and preorder entries.
 *
 * Reports codes rather than the error copy the buyer read: messages are reworded
 * freely and some come from a Formik schema, so text would be unqueryable across
 * releases and a standing risk of carrying what the buyer typed into a property.
 */
const ORDER_VALIDATION_FAILURE = {
    cartId: z.string().describe('The cart whose order was blocked.'),
    totalCartCost: z.number().describe('Cart total in USD at the moment of the attempt.'),
    failedSections: z
        .array(z.enum(Object.values(CHECKOUT_ERROR_SECTIONS)))
        .describe('Which parts of checkout blocked the order.'),
    errorCodes: z
        .array(z.enum(Object.values(CHECKOUT_VALIDATION_ERROR_CODES)))
        .describe('The individual checks that failed.'),
    failureClass: z
        .enum(['user_fixable', 'system'])
        .describe('Whether the buyer could act on this. `system` means only tax or fee calculation failed, so a ready buyer was blocked by Ekho rather than by their own form.'),
    attemptNumber: z
        .number()
        .describe('Which blocked attempt this is for the visit, counting from 1 and stopping at 10.'),
};
/** The client surfaces engagement events originate from. */
const SURFACE = {
    Carthage: 'carthage',
    Checkout: 'checkout',
    BuyerPortal: 'buyer-portal',
    AdminPortal: 'admin-portal',
    SalesAgent: 'sales-agent',
    Sdk: 'sdk',
};
/**
 * Which address the buyer is filling in. The `AddressInput` component renders on
 * several checkout surfaces, so every address event carries this to stay
 * attributable without needing a separate event name per surface.
 *
 * `fulfillment` is the Express delivery-address surface. It is a valid value
 * ahead of its call sites being wired, so the Express spec can point at these
 * events rather than duplicating them.
 */
/** The cart an event belongs to. Present on every checkout event so a report can
 * reach the vehicle, dealer and order without joining through the session. */
const CART_ID = z.string().describe('The cart the buyer is working on.');
/**
 * The configurable vehicle a configurator event concerns. The configurator runs
 * before a cart exists, so this is the only join key those events can carry —
 * `cartId` appears on the handoff events alone, once there is a cart to name.
 */
const CONFIGURATOR_PRODUCT_ID = z.string().describe('The vehicle product being configured.');
/**
 * Which funnel the buyer was in. Carried by the support events alone: they are
 * one control serving both flows, so unlike the funnel steps they are genuinely
 * one event with a discriminator rather than a `co_`/`po_` pair.
 */
const CHECKOUT_SESSION_TYPE = z
    .enum(['checkout', 'preorder'])
    .describe('Which funnel the buyer was in when they asked for help.');
/**
 * Why a valid submission failed after the buyer committed to it.
 *
 * Distinct from `CHECKOUT_VALIDATION_ERROR_CODES`, which covers a submission
 * checkout refused to accept: these are the ways an accepted one fell over
 * afterwards, and the split matters because one is form friction the buyer can
 * fix and the other is Ekho or a processor failing them mid-purchase.
 *
 * The strings keep their existing spelling rather than being normalised to the
 * SCREAMING_SNAKE used elsewhere. They have been emitting as free text for a
 * year; bounding the set costs nothing, while respelling the values would
 * silently break every dashboard filtering on them.
 */
const ORDER_FAILURE_REASON = z
    .enum([
    'initialSetupFailed',
    'orderPreparationFailed',
    'databaseOperationsFailed',
    'paymentProcessingFailed',
    'paymentFailed',
    'stripeValidationFailed',
    'stripeElementsUpdateFailed',
    'stripeElementsValidationFailed',
    'placedOrderIdNull',
    'dealerLeadCheckFailed',
    'dealerLeadRedirectFailed',
])
    .describe('Which stage of order placement failed. A bounded code, never the error copy.');
/** A submission that failed after the buyer committed to it. */
const SUBMISSION_FAILURE = {
    cartId: CART_ID,
    totalCartCost: z.number().describe('Cart total in USD at the moment of the attempt.'),
};
/**
 * A cart left with work in progress, shared by the checkout and preorder
 * entries so the two funnels report abandonment identically.
 *
 * `section` is the point the buyer walked away from, and is most of the value
 * here: leaving the configurator is browsing, while leaving a part-filled
 * financing application is a lead worth recovering, and a single abandonment
 * count blends the two into a number nobody can act on.
 *
 * The cart contents are a count plus ids rather than the comma-joined list of
 * display names this event used to send. That string could not be grouped,
 * filtered, or joined to inventory, and it broke whenever a product was
 * renamed.
 */
const ABANDONED_CART = {
    cartId: z
        .string()
        .optional()
        .describe('The cart the buyer left. Absent when they left the configurator, where a build exists but no cart has been created yet.'),
    page: z
        .enum(Object.values(CHECKOUT_PAGES))
        .describe('Which page of checkout the buyer was on when they left.'),
    numVehicles: z.number().int().describe('How many vehicles were in the cart.'),
    numAccessories: z.number().int().describe('How many accessories were in the cart.'),
    vehicleProductIds: z
        .array(z.string())
        .describe('Product id of each vehicle left behind, for joining to inventory.'),
    accessoryIds: z
        .array(z.string())
        .describe('Id of each accessory left behind. Populated from the configurator too, where the accessories are held against a build rather than a cart.'),
    totalCartCost: z
        .number()
        .optional()
        .describe('Cart total in USD when they left. Absent when the buyer left the configurator before checkout resolved any totals.'),
};
/**
 * One accessory moving in or out of a build, shared by the add and remove
 * entries so the pair stays queryable as a single set-membership stream.
 *
 * Both sides carry the same shape deliberately. A buyer can remove a required
 * default they never added, so a remove is not always preceded by an add, and a
 * narrower remove schema would leave those accessories undescribed.
 *
 * There is no `isAvailable`: an unavailable option renders disabled and cannot be
 * picked, so the field could only ever report `true`.
 */
const CONFIGURATOR_ACCESSORY = {
    productId: CONFIGURATOR_PRODUCT_ID,
    categoryId: z.string().describe('The option category the accessory sits in.'),
    categoryName: z.string().describe('Display name of the option category.'),
    accessoryId: z.string().describe('The accessory that moved.'),
    accessoryName: z.string().describe('Display name of the accessory.'),
    accessoryPrice: z.number().describe('Accessory price in USD at the moment of the action.'),
    stock: z
        .number()
        .optional()
        .describe('Units on hand. Absent when the catalog tracks no stock for this accessory.'),
    isBackorder: z
        .boolean()
        .describe('Whether the accessory is backordered, so taking it means accepting a wait.'),
    isCompatible: z
        .boolean()
        .optional()
        .describe('Whether the accessory is compatible with the rest of the build. Absent when the catalog computes no compatibility for it.'),
};
const ENGAGEMENT_EVENTS = {
    // -------------------------------------------------------------------------
    // Carthage (consumer search/listing portal)—Segment
    // -------------------------------------------------------------------------
    CARTHAGE_LISTING_VIEWED: {
        canonical: 'carth_listing_viewed',
        wireName: 'carthage_listing_viewed',
        surface: SURFACE.Carthage,
        description: 'Buyer viewed a vehicle detail page.',
        destinations: CLIENT_AD_EVENT_DESTINATIONS,
        adEventNames: {
            meta: 'ViewContent',
            tiktok: 'ViewContent',
            tracklution: null,
            gtm: 'view_item',
        },
        schema: z.object({
            listingId: z.string(),
            make: z.string(),
            model: z.string(),
            modelYear: z.number(),
            listingPrice: z.number(),
            type: z.string(),
            condition: z.string(),
            stockNumber: z.string().optional(),
            vin: z.string().optional(),
            odometerReading: z.number().optional(),
            odometerUnit: z.string().optional(),
            hasMonthlyPayment: z.boolean(),
            minMonthlyPayment: z.number(),
            totalImages: z.number(),
            isPurchasableOnline: z
                .boolean()
                .optional()
                .describe('Whether the page offers online checkout for the unit, by the same rule as the Buy now button. Absent when the inventory owner lookup failed.'),
            onlineSaleStatus: z
                .nativeEnum(OnlineSaleStatus)
                .optional()
                .describe("The listing's online-sale status at view time. Absent when the inventory owner lookup failed."),
        }),
    },
    CARTHAGE_PAYMENT_SELECTION_CHANGED: {
        canonical: 'carth_payment_selection_changed',
        wireName: 'carthage_payment_selection_changed',
        surface: SURFACE.Carthage,
        description: 'Buyer toggled between monthly and one-time pricing.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Payment Selection Changed' },
        schema: z.object({
            from: z.string(),
            to: z.string(),
            listingId: z.string(),
        }),
    },
    CARTHAGE_CALCULATE_PAYMENT_OPENED: {
        canonical: 'carth_calculate_payment_opened',
        wireName: 'carthage_calculate_payment_opened',
        surface: SURFACE.Carthage,
        description: 'Buyer opened the payment calculator.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Calculate Payment Opened' },
        schema: z.object({
            listingId: z.string().optional(),
            location: z.string().optional(),
        }),
    },
    CARTHAGE_SEARCH_PERFORMED: {
        canonical: 'carth_search_performed',
        wireName: 'carthage_search_performed',
        surface: SURFACE.Carthage,
        description: 'Buyer executed a search.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Search Performed' },
        schema: z.object({
            query: z.string().optional(),
        }),
    },
    CARTHAGE_PREQUAL_MODAL_OPENED: {
        canonical: 'carth_prequal_modal_opened',
        wireName: 'carthage_prequal_modal_opened',
        surface: SURFACE.Carthage,
        description: 'Buyer opened the pre-qualification modal.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Pre-Qualified Modal Opened' },
        schema: z.object({
            location: z.string(),
        }),
    },
    CARTHAGE_HOW_TO_BUY_CLICKED: {
        canonical: 'carth_how_to_buy_clicked',
        wireName: 'carthage_how_to_buy_clicked',
        surface: SURFACE.Carthage,
        description: 'Buyer opened the How to Buy explainer.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'How to Buy Clicked' },
        schema: z.object({
            listingId: z.string(),
            location: z.string(),
        }),
    },
    CARTHAGE_IMAGE_GALLERY_OPENED: {
        canonical: 'carth_image_gallery_opened',
        wireName: 'carthage_image_gallery_opened',
        surface: SURFACE.Carthage,
        description: 'Buyer opened the image gallery.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Image Gallery Opened' },
        schema: z.object({
            listingId: z.string(),
            totalImages: z.number(),
        }),
    },
    CARTHAGE_IMAGE_VIEWED: {
        canonical: 'carth_image_viewed',
        wireName: 'carthage_image_viewed',
        surface: SURFACE.Carthage,
        description: 'Buyer viewed a listing image.',
        schema: z.object({
            vehicleId: z.string(),
            imageIndex: z.number(),
            totalImages: z.number(),
            make: z.string().optional(),
            model: z.string().optional(),
            modelYear: z.number().optional(),
            viewMode: z.literal('lightbox'),
        }),
    },
    CARTHAGE_GET_DIRECTIONS_CLICKED: {
        canonical: 'carth_get_directions_clicked',
        wireName: 'carthage_get_directions_clicked',
        surface: SURFACE.Carthage,
        description: 'Buyer opened directions to the dealership.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Get Directions Clicked' },
        schema: z.object({
            listingId: z.string(),
            address: z.string(),
        }),
    },
    CARTHAGE_VIEW_ALL_INVENTORY_CLICKED: {
        canonical: 'carth_view_all_inventory_clicked',
        wireName: 'carthage_view_all_inventory_clicked',
        surface: SURFACE.Carthage,
        description: 'Buyer clicked View All Inventory.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'View All Inventory Clicked' },
        schema: z.object({
            location: z.string(),
            // Absent when the click happens outside a listing context.
            fromListingId: z.string().optional(),
        }),
    },
    CARTHAGE_VIEW_MAP_CLICKED: {
        canonical: 'carth_view_map_clicked',
        wireName: 'carthage_view_map_clicked',
        surface: SURFACE.Carthage,
        description: 'Buyer opened the dealership map.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'View Map Clicked' },
        schema: z.object({
            location: z.string(),
            address: z.string(),
        }),
    },
    CARTHAGE_PHONE_CLICKED: {
        canonical: 'carth_phone_clicked',
        wireName: 'carthage_phone_clicked',
        surface: SURFACE.Carthage,
        description: 'Buyer clicked the dealership phone number.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Phone Click' },
        schema: z.object({
            location: z.string(),
            phone: z.string(),
        }),
    },
    CARTHAGE_INVENTORY_CARD_CLICKED: {
        canonical: 'carth_inventory_card_clicked',
        wireName: 'carthage_inventory_card_clicked',
        surface: SURFACE.Carthage,
        description: 'Buyer clicked an inventory card.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Inventory Card Clicked' },
        schema: z.object({
            listingId: z.string(),
            fromListingId: z.string().optional(),
            location: z.string(),
            make: z.string(),
            model: z.string(),
            modelYear: z.number(),
            advertisedPrice: z.number(),
            vehicleCategory: z.string(),
            condition: z.string(),
            positionInList: z.number(),
            minMonthlyPayment: z.number(),
            odometerReading: z.number().optional(),
            city: z.string().optional(),
            state: z.string().optional(),
            hasMonthlyPayment: z.boolean(),
        }),
    },
    CARTHAGE_HEADER_LINK_CLICKED: {
        canonical: 'carth_header_link_clicked',
        wireName: 'carthage_header_link_clicked',
        surface: SURFACE.Carthage,
        description: 'Buyer clicked a header link.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Header Link Clicked' },
        schema: z.object({
            linkLabel: z.string(),
            linkType: z.string(),
        }),
    },
    CARTHAGE_SEARCH_COMPLETED: {
        canonical: 'carth_search_completed',
        wireName: 'carthage_search_completed',
        surface: SURFACE.Carthage,
        description: 'Buyer selected a search result.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Search Completed' },
        schema: z.object({
            query: z.string(),
            resultId: z.string().optional(),
        }),
    },
    CARTHAGE_LOGO_CLICKED: {
        canonical: 'carth_logo_clicked',
        wireName: 'carthage_logo_clicked',
        surface: SURFACE.Carthage,
        description: 'Buyer clicked the store logo.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Logo Clicked' },
        schema: z.object({
            destination: z.string(),
            location: z.string(),
        }),
    },
    CARTHAGE_FOOTER_LINK_CLICKED: {
        canonical: 'carth_footer_link_clicked',
        wireName: 'carthage_footer_link_clicked',
        surface: SURFACE.Carthage,
        description: 'Buyer clicked a footer link.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Footer Link Clicked' },
        schema: z.object({
            linkLabel: z.string(),
            linkUrl: z.string(),
            linkGroup: z.string().optional(),
        }),
    },
    CARTHAGE_INQUIRY_MODAL_OPENED: {
        canonical: 'carth_inquiry_modal_opened',
        wireName: 'carthage_inquiry_modal_opened',
        surface: SURFACE.Carthage,
        description: 'Buyer opened the lead-inquiry modal.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'Inquiry Modal Opened' },
        schema: z.object({
            listingId: z.string().optional(),
            location: z.string(),
            make: z.string().optional(),
            model: z.string().optional(),
            modelYear: z.number().optional(),
            advertisedPrice: z.number().optional(),
            vehicleCategory: z.string().optional(),
            condition: z.string().optional(),
        }),
    },
    CARTHAGE_FILTER_APPLIED: {
        canonical: 'carth_filter_applied',
        wireName: 'carthage_filter_applied',
        surface: SURFACE.Carthage,
        description: 'Buyer applied a search filter.',
        schema: z.object({
            filterName: z.string(),
            filterValue: z.string(),
            totalResultsBefore: z.number(),
            activeFilterCount: z.number(),
        }),
    },
    CARTHAGE_FILTER_CLEARED: {
        canonical: 'carth_filter_cleared',
        wireName: 'carthage_filter_cleared',
        surface: SURFACE.Carthage,
        description: 'Buyer cleared a search filter.',
        schema: z.object({
            filterName: z.string(),
            totalResultsBefore: z.number(),
            activeFilterCount: z.number(),
        }),
    },
    CARTHAGE_SORT_CHANGED: {
        canonical: 'carth_sort_changed',
        wireName: 'carthage_sort_changed',
        surface: SURFACE.Carthage,
        description: 'Buyer changed the search sort order.',
        schema: z.object({
            sortValue: z.string(),
            previousSortValue: z.string(),
            totalResults: z.number(),
        }),
    },
    CARTHAGE_PAGINATION_CHANGED: {
        canonical: 'carth_pagination_changed',
        wireName: 'carthage_pagination_changed',
        surface: SURFACE.Carthage,
        description: 'Buyer changed the search-results page.',
        schema: z.object({
            pageNumber: z.number(),
            previousPage: z.number(),
            itemsPerPage: z.number(),
            totalResults: z.number(),
        }),
    },
    CARTHAGE_CTA_BUTTON_CLICKED: {
        canonical: 'carth_cta_button_clicked',
        wireName: 'carthage_cta_button_clicked',
        surface: SURFACE.Carthage,
        description: 'Buyer pressed a CTA button.',
        destinations: DEALER_DATALAYER_DESTINATIONS,
        adEventNames: { gtm: 'CTA Button Clicked' },
        schema: z.object({
            label: z.string(),
            itemId: z.string(),
            location: z.string(),
            component: z.string(),
            listingId: z.string().optional(),
            actionType: z.enum(['none', 'url', 'contact_picker', 'iframe_modal']),
        }),
    },
    // -------------------------------------------------------------------------
    // Checkout (vehicle purchase/reservation flow)—Segment
    // -------------------------------------------------------------------------
    /**
     * The model year is part of the product id, so `modelYear` is a convenience
     * rather than new information—it saves every report parsing four characters
     * off the end of a string.
     *
     * Carries nothing about the incentive badged on the year. The promotions that
     * actually reach an order are resolved server-side and reported by the
     * `co_promotion_*` events once a cart exists; a campaign snapshot taken here
     * would describe what was advertised, not what applied, and the two diverge.
     * @dedupe Capped at 5 per visit.
     */
    CONFIGURATOR_MODEL_YEAR_SELECTED: {
        canonical: 'co_configurator_model_year_selected',
        wireName: 'configurator_model_year_selected',
        surface: SURFACE.Checkout,
        description: 'Buyer moved the configurator to a different model year. Fires on a real change only, never on re-picking the year already selected.',
        schema: z.object({
            productId: CONFIGURATOR_PRODUCT_ID,
            modelYear: z
                .number()
                .int()
                .describe('The model year moved to, so a report groups without parsing the product id.'),
        }),
    },
    /** @dedupe Capped at 5 per visit. */
    CONFIGURATOR_VEHICLE_SELECTED: {
        canonical: 'co_configurator_vehicle_selected',
        wireName: 'configurator_vehicle_selected',
        surface: SURFACE.Checkout,
        description: 'Buyer moved the configurator to a different vehicle. Fires on a real change only, never on re-picking the vehicle already selected.',
        schema: z.object({
            productId: CONFIGURATOR_PRODUCT_ID,
            vehicleName: z.string().describe('Catalog model name of the vehicle moved to.'),
            vehiclePrice: z
                .number()
                .optional()
                .describe('Retail price in USD. Absent when the catalog carries no retail price.'),
        }),
    },
    /** @dedupe Capped at 5 per product/category pair. */
    CONFIGURATOR_ACCESSORY_ADDED: {
        canonical: 'co_configurator_accessory_added',
        wireName: 'configurator_accessory_selected',
        surface: SURFACE.Checkout,
        description: 'Buyer added an accessory to the build.',
        schema: z.object({ ...CONFIGURATOR_ACCESSORY }),
    },
    /** @dedupe Capped at 5 per product/category pair. */
    CONFIGURATOR_ACCESSORY_REMOVED: {
        canonical: 'co_configurator_accessory_removed',
        wireName: 'configurator_accessory_deselected',
        surface: SURFACE.Checkout,
        description: 'Buyer took an accessory back off the build. Reachable only in categories the vehicle does not require.',
        schema: z.object({ ...CONFIGURATOR_ACCESSORY }),
    },
    /**
     * The handoff, reported from the press rather than from its outcome: the click
     * is the whole signal, and where it lands is `co_checkout_started`.
     *
     * `promotionType` and `promotionAmount` are omitted rather than sent as `null`
     * and `0` when nothing is active, so a genuinely zero-valued promotion stays
     * distinguishable from no promotion at all. Their enum members keep the
     * `*_DISCOUNT` spelling because that is the stored constant; only the property
     * names follow the promotion vocabulary the product uses.
     * @dedupe Not deduped.
     */
    CONFIGURATOR_CONTINUE_CLICKED: {
        canonical: 'co_configurator_continue_clicked',
        wireName: 'configurator_continue_to_checkout_clicked',
        surface: SURFACE.Checkout,
        description: 'Buyer pressed Continue to hand their configured build off to checkout.',
        schema: z.object({
            productId: CONFIGURATOR_PRODUCT_ID,
            basePrice: z.number().describe('Vehicle base price in USD, before accessories.'),
            totalAccessoriesCost: z.number().describe('Sum of the selected accessory prices in USD.'),
            totalPrice: z.number().describe('Build price in USD after any active promotion.'),
            accessoryCount: z
                .number()
                .int()
                .describe('How many accessories are on the build. Duplicates `configuration.length` so the common aggregation does not have to unnest the array.'),
            promotionType: DiscountTypesSchema.optional().describe('How the active promotion reduces the price. Absent when no promotion is active.'),
            promotionAmount: z
                .number()
                .optional()
                .describe('The active promotion’s value, a percentage or a USD figure depending on `promotionType`. Absent when no promotion is active.'),
            configuration: z
                .array(z.object({
                categoryId: z.string(),
                categoryName: z.string().optional(),
                accessoryId: z.string(),
                price: z.number(),
                required: z.boolean(),
            }))
                .describe('Every option category on the build and the accessory chosen for it.'),
        }),
    },
    /**
     * The handoff failing at cart creation: the buyer pressed Continue, the
     * request to build their cart was rejected, and they stayed on the
     * configurator watching the spinner stop.
     *
     * Scoped to creation on purpose. The other way a handoff can break is the
     * cart failing to *load* after the redirect, and that one is unreportable
     * from the client: checkout resolves its OEM from the cart, so a cart that
     * never loads means the analytics facade never binds and anything fired from
     * that path is buffered and never sent. Declaring a `cart_load_failed` reason
     * would promise data that cannot exist. Those failures are visible
     * server-side, where the rejected request is already logged.
     *
     * There is no success counterpart either. A completed handoff is
     * `co_checkout_started` in the same session, so an event for it would report
     * a transition the stream already implies.
     * @dedupe Capped at 10 per visit.
     */
    CONFIGURATOR_CONTINUE_FAILED: {
        canonical: 'co_configurator_continue_failed',
        wireName: 'configurator_continue_to_checkout_failed',
        surface: SURFACE.Checkout,
        description: 'Buyer pressed Continue and their cart could not be created, so they never left the configurator.',
        schema: z.object({
            productId: CONFIGURATOR_PRODUCT_ID,
            totalPrice: z.number().describe('Build price in USD at the moment of the attempt.'),
            failureReason: z
                .enum(['cart_create_failed'])
                .describe('Where the handoff broke. Creation is the only client-observable failure.'),
            attemptNumber: z
                .number()
                .int()
                .describe('Which failed attempt this is for the visit, counting from 1 and stopping at 10.'),
        }),
    },
    /** @dedupe Not deduped. */
    TRACK_BUYER_CLICKED_LOG_IN_WITHIN_CHECKOUT: {
        canonical: 'co_login_link_clicked',
        wireName: 'track_buyer_clicked_log_in_within_checkout',
        surface: SURFACE.Checkout,
        description: 'Buyer clicked the log-in link under the contact section, which hands off to the buyer portal.',
        schema: z.object({ cartId: CART_ID }),
    },
    CHECKOUT_FINANCING_MODAL_OPENED: {
        canonical: 'co_financing_calculator_opened',
        wireName: 'checkout_financing_modal_opened',
        surface: SURFACE.Checkout,
        description: 'Buyer opened the in-checkout financing-details modal, the estimator that shows the APR, term, down payment, and monthly payment for the active vehicle.',
        schema: z.object({
            cartId: CART_ID,
            totalCartCost: z.number().describe('Cart total in USD when the calculator opened.'),
            vehicleProductId: z.string().describe('Product id of the vehicle being financed.'),
            vehicleVariantId: z.string().describe('Variant id of the vehicle being financed.'),
            vehicleName: z.string().describe('Display name of the vehicle being financed.'),
            vehicleBasePrice: z.number().describe('Base price of the vehicle in USD.'),
        }),
    },
    /**
     * The estimate in the financing accordion's header, which opens a breakdown of
     * how the monthly figure is reached.
     *
     * A separate entry from `co_financing_calculator_opened` because they are
     * different controls opening different overlays: this is the headline number
     * a buyer taps to ask "how is that calculated", while the calculator is the
     * link in the accordion body. Collapsing them would hide which of the two the
     * buyer actually reaches for.
     * @dedupe Not deduped.
     */
    FINANCING_ESTIMATE_BREAKDOWN_OPENED: {
        canonical: 'co_financing_estimate_breakdown_opened',
        surface: SURFACE.Checkout,
        description: 'Buyer opened the breakdown behind the estimated monthly payment shown on the financing option.',
        schema: z.object({
            cartId: CART_ID,
            monthlyPayment: z.number().describe('The estimated monthly figure they tapped, in USD.'),
            apr: z.number().optional().describe('APR behind the estimate. Absent when none resolved.'),
            termMonths: z
                .number()
                .int()
                .optional()
                .describe('Term behind the estimate. Absent when none resolved.'),
            downPayment: z
                .number()
                .optional()
                .describe('Down payment behind the estimate. Absent when none resolved.'),
        }),
    },
    /**
     * The handoff into the bank-linking provider's own flow. `_clicked` rather
     * than `_opened`: what happens next is a third party's screen, so the click
     * is the whole of what this surface can honestly report.
     * @dedupe Not deduped.
     */
    BANK_SEARCH_CLICKED: {
        canonical: 'co_bank_search_clicked',
        surface: SURFACE.Checkout,
        description: 'Buyer pressed the control that opens bank search, handing off to the bank-linking provider.',
        schema: z.object({ cartId: CART_ID }),
    },
    CHECKOUT_PREQUAL_MODAL_OPENED: {
        canonical: 'co_prequal_modal_opened',
        wireName: 'checkout_prequal_modal_opened',
        surface: SURFACE.Checkout,
        description: 'Buyer opened the pre-qualification modal.',
        schema: z.object({
            cartId: CART_ID,
            totalCartCost: z.number().describe('Cart total in USD when it opened.'),
        }),
    },
    /**
     * The pre-qualification pair, mirroring what every other surface running a
     * soft pull already reports. Checkout opened its modal and reported nothing
     * further, so the share of buyers who consent, and what the engine decided
     * about them, were unanswerable on the surface that sells the most.
     *
     * The backend records every submission through the shared prequal service, but
     * with no surface and no session id, so it cannot say whether a decision came
     * from checkout, Express, or the sales agent. These two are the client-side,
     * session-joinable half of that record.
     */
    CHECKOUT_PREQUAL_SUBMITTED: {
        canonical: 'co_prequal_submitted',
        surface: SURFACE.Checkout,
        description: 'Buyer submitted the pre-qualification form, consenting to the soft pull. `co_prequal_modal_opened` minus this event is the population that opened the modal and backed out. Un-deduped, capped at 3 firings per visit: a retry after an error is a real repeat.',
        schema: z.object({
            cartId: CART_ID,
            disclosureVersion: z
                .string()
                .describe('Version identifier of the FCRA consent text the buyer was shown.'),
        }),
    },
    CHECKOUT_PREQUAL_RESULT_RECEIVED: {
        canonical: 'co_prequal_result_received',
        surface: SURFACE.Checkout,
        description: 'The credit engine returned a decision on the buyer. A call that errored instead of deciding reports `co_prequal_failed`, so this event means a verdict exists. Once per submission.',
        schema: z.object({
            cartId: CART_ID,
            outcome: PREQUAL_OUTCOME,
            rejectionReasons: z
                .array(z.string())
                .describe('Why the engine declined, as it reported them. Empty on an approval. Carried because "how many buyers were denied" is answerable without it and "why" is not.'),
            prequalId: z.string().describe('The lender file number for this decision.'),
        }),
    },
    CHECKOUT_PREQUAL_FAILED: {
        canonical: 'co_prequal_failed',
        surface: SURFACE.Checkout,
        description: 'The pre-qualification request never reached the credit bureaus, so the buyer was shown a denial the engine never issued. Distinct from `co_prequal_result_received` with a `DENIED` outcome, which is a real verdict: this one is an outage wearing a decision, and counting the two together makes an availability problem look like a credit-quality problem. Un-deduped, capped at 3 firings per visit.',
        schema: z.object({
            cartId: CART_ID,
            errorType: z
                .enum(['synthetic_denial'])
                .describe('How the call failed. `synthetic_denial` is the only case checkout can see: the request never reached the bureaus, so the app renders a denial it did not receive.'),
            attempt: z.number().int().describe('Which attempt this was, counting from one.'),
        }),
    },
    /**
     * Every move between checkout's pages, naming where the buyer arrived.
     *
     * One event rather than a pair of enter/leave verbs, and one event rather
     * than one per page: the sequence of arrivals is the route the buyer took, so
     * a reader gets the path without re-pairing anything or unioning several
     * entries. Direction is the relationship between consecutive arrivals, which
     * is why there is no direction property—it would describe the previous
     * event rather than this one.
     *
     * Reported where the rendered step actually changes, so every control that
     * moves the buyer is covered by construction: the breadcrumbs, the footer's
     * back button, and the Continue press that diverts into the credit
     * application.
     *
     * Not an abandonment. The buyer is still in checkout; `co_cart_abandoned` is
     * for leaving it. Not `co_checkout_started` either, which marks the surface
     * becoming usable rather than movement within it—so arriving at the first
     * page is not reported here.
     * @dedupe Capped at 5 per visit.
     */
    CHECKOUT_PAGES_MOVED: {
        canonical: 'co_checkout_pages_moved',
        surface: SURFACE.Checkout,
        description: 'Buyer moved to a different page of checkout. Reports where they arrived; the sequence of these is the route they took.',
        schema: z.object({
            cartId: CART_ID,
            page: z
                .enum(Object.values(CHECKOUT_PAGES))
                .describe('The page the buyer arrived at.'),
            fromPage: z
                .enum(Object.values(CHECKOUT_PAGES))
                .describe('The page they left. Carried so a single row describes the move, rather than needing the preceding event to interpret it.'),
        }),
    },
    /** @dedupe Capped at 10 per visit. */
    ORDER_VALIDATION_FAILED: {
        canonical: 'co_order_validation_failed',
        wireName: 'order_validation_failed',
        surface: SURFACE.Checkout,
        description: 'Buyer pressed Place Order and checkout refused to submit it, showing them what to fix. Distinct from `co_order_failed`, which covers a valid submission that failed afterwards.',
        schema: z.object({ ...ORDER_VALIDATION_FAILURE }),
    },
    PREORDER_VALIDATION_FAILED: {
        canonical: 'po_order_validation_failed',
        surface: SURFACE.Checkout,
        description: 'Preorder buyer pressed Place Preorder and checkout refused to submit it, showing them what to fix. Distinct from `po_order_failed`, which covers a valid submission that failed afterwards.',
        schema: z.object({ ...ORDER_VALIDATION_FAILURE }),
    },
    /**
     * Renamed to the `po_` funnel prefix, keeping `wireName` so the deposit-valued
     * ad conversion built on the emitted string survives the rename.
     * @dedupe Not deduped.
     */
    PREORDER_PLACED_FAILED: {
        canonical: 'po_order_failed',
        wireName: 'preorder_placed_failed',
        surface: SURFACE.Checkout,
        description: 'Preorder buyer submitted a valid preorder and it failed afterwards. Distinct from `po_order_validation_failed`, which covers one checkout refused to submit.',
        schema: z.object({ ...SUBMISSION_FAILURE, failureReason: ORDER_FAILURE_REASON }),
    },
    /**
     * `failureReason` is a bounded code rather than the caught exception's
     * `message`, which is what this event used to send. An exception string is
     * unqueryable, changes with any dependency upgrade, and can interpolate
     * whatever the buyer typed into a CDP property. The exception itself is still
     * captured by the `createLog` call alongside each emitter, which is where
     * debugging detail belongs.
     * @dedupe Not deduped.
     */
    SUBMIT_CREDIT_APP_FAILED: {
        canonical: 'co_credit_app_failed',
        wireName: 'submit_credit_app_failed',
        surface: SURFACE.Checkout,
        description: 'Credit-application submission failed.',
        schema: z.object({
            ...SUBMISSION_FAILURE,
            failureReason: z
                .enum(['validationFailed', 'submissionFailed'])
                .describe('Whether the application was rejected by its own validation before being sent, or the send itself failed.'),
        }),
    },
    /** @dedupe Not deduped. */
    ORDER_PLACED_FAILED: {
        canonical: 'co_order_failed',
        wireName: 'order_placed_failed',
        surface: SURFACE.Checkout,
        description: 'Buyer submitted a valid order and it failed afterwards. Distinct from `co_order_validation_failed`, which covers one checkout refused to submit.',
        schema: z.object({ ...SUBMISSION_FAILURE, failureReason: ORDER_FAILURE_REASON }),
    },
    /** @dedupe Capped at 5 per visit. */
    TAR_OPTION_SELECTED: {
        canonical: 'co_tar_option_selected',
        wireName: 'tar_option_selected',
        surface: SURFACE.Checkout,
        description: 'Buyer chose how their title and registration paperwork is handled. Never fires for the option preselected once the supported set resolves.',
        schema: z.object({
            cartId: z.string().describe('The cart being registered.'),
            tarOption: TAR_OPTION.describe('The paperwork option the buyer moved to.'),
        }),
    },
    PAYMENT_OPTION_SELECTED: {
        canonical: 'co_payment_option_selected',
        wireName: 'payment_selection_selected',
        surface: SURFACE.Checkout,
        description: 'Buyer chose a payment option themselves. Never fires for the option preselected at load, which rides `co_checkout_started.defaultPaymentOption` instead.',
        schema: z.object({
            cartId: z.string().describe('The cart the buyer is paying for.'),
            paymentSelection: CHECKOUT_PAYMENT_OPTION.describe('The payment option the buyer moved to.'),
        }),
    },
    PREORDER_PAYMENT_OPTION_SELECTED: {
        canonical: 'po_payment_option_selected',
        surface: SURFACE.Checkout,
        description: 'Preorder buyer chose a reservation option themselves (standard or VIP). Never fires for the option preselected at load, which rides `po_checkout_started.defaultPaymentOption` instead.',
        schema: z.object({
            cartId: z.string().describe('The preorder cart the buyer is reserving against.'),
            paymentSelection: PREORDER_PAYMENT_OPTION.describe('The reservation option the buyer moved to.'),
        }),
    },
    /** @dedupe Capped at 5 per visit. */
    PAYMENT_OPTION_OVERRIDDEN: {
        canonical: 'co_payment_option_overridden',
        surface: SURFACE.Checkout,
        description: 'Checkout moved the buyer off the payment option they held, because it stopped being offered. The buyer chose nothing here—a toast tells them it happened.',
        schema: z.object({
            cartId: z.string().describe('The cart whose payment option was changed.'),
            from: CHECKOUT_PAYMENT_OPTION.describe('The option the buyer had.'),
            to: CHECKOUT_PAYMENT_OPTION.describe('The option checkout moved them to.'),
            reason: z
                .enum(['option_withdrawn', 'payment_intent_unavailable'])
                .describe('Why it stopped being offered. `payment_intent_unavailable` means the Stripe intent failed to create, which is an incident rather than a business rule, and is the value worth alerting on.'),
        }),
    },
    /** @dedupe Capped at 5 per visit. */
    DELIVERY_OPTION_OVERRIDDEN: {
        canonical: 'co_delivery_option_overridden',
        surface: SURFACE.Checkout,
        description: 'Checkout moved the buyer off the fulfilment option they held, because it stopped being offered. The buyer chose nothing here, and unlike the payment case nothing tells them it happened.',
        schema: z.object({
            cartId: z.string().describe('The cart whose fulfilment option was changed.'),
            from: DELIVERY_OPTION.describe('The option the buyer had.'),
            to: DELIVERY_OPTION.describe('The option checkout moved them to.'),
            reason: z.enum(['option_withdrawn']).describe('Why it stopped being offered.'),
        }),
    },
    /** @dedupe Capped at 5 per visit. */
    PICKUP_LOCATION_SELECTED: {
        canonical: 'co_pickup_location_selected',
        surface: SURFACE.Checkout,
        description: 'Buyer picked a pickup location from the ones offered. Never fires when the location was auto-assigned, since the buyer made no choice there.',
        schema: z.object({
            cartId: z.string().describe('The cart the buyer is collecting.'),
            locationId: z.string().describe('The pickup location chosen.'),
            locationName: z.string().describe('Display name of the location chosen.'),
            source: z
                .enum(['list', 'map'])
                .describe('Which control the buyer picked it from—the list of rows, or a map marker.'),
            rank: z
                .number()
                .optional()
                .describe('Its position in the offered list, counting from 1. Absent when the chosen location is not in the offered list, which happens for a single seller-fulfilment location.'),
            locationCount: z.number().describe('How many locations were on offer.'),
            distanceMiles: z
                .number()
                .optional()
                .describe('Whole miles from the buyer to the location. Absent when unmeasurable: no origin, or a location without coordinates.'),
            previousLocationId: z
                .string()
                .optional()
                .describe('The location they moved off. Absent on their first pick of the visit.'),
        }),
    },
    /** @dedupe Capped at 5 per visit. */
    PICKUP_LOCATION_CLEARED: {
        canonical: 'co_pickup_location_cleared',
        surface: SURFACE.Checkout,
        description: 'Buyer dropped the pickup location they had, leaving none selected. Reported so the stream stops claiming a location the buyer no longer holds.',
        schema: z.object({
            cartId: z.string().describe('The cart the buyer is collecting.'),
            locationId: z.string().describe('The pickup location given up.'),
            locationName: z.string().describe('Display name of the location given up.'),
            source: z
                .enum(['list', 'map', 'remove_link'])
                .describe('Which control dropped it. `remove_link` is the Remove link beside the chosen location, which can only clear.'),
        }),
    },
    /** @dedupe Capped at 5 per visit. */
    DELIVERY_OPTION_SELECTED: {
        canonical: 'co_delivery_option_selected',
        wireName: 'delivery_option_selected',
        surface: SURFACE.Checkout,
        description: 'Buyer chose a delivery option themselves. Never fires for the option preselected at load, which rides `co_checkout_started.defaultDeliveryOption` instead.',
        schema: z.object({
            cartId: z.string().describe('The cart the buyer is arranging fulfilment for.'),
            deliveryOption: DELIVERY_OPTION.describe('The fulfilment option the buyer moved to.'),
        }),
    },
    LEGAL_LINK_CLICKED: {
        canonical: 'co_legal_link_clicked',
        wireName: 'terms_link_clicked',
        surface: SURFACE.Checkout,
        description: 'Buyer opened one of the legal or help documents linked from checkout. Each opens an external page in a new tab, so the click is the whole of what happened here.',
        schema: z.object({
            cartId: CART_ID,
            link: z
                .enum(['terms', 'privacy', 'faq'])
                .describe('Which document they opened. One entry rather than three: same trigger, same payload, same reader.'),
        }),
    },
    DISCLOSURES_TOGGLED: {
        canonical: 'co_disclosures_toggled',
        wireName: 'buyer_agreed_to_disclosures',
        surface: SURFACE.Checkout,
        description: 'Buyer ticked or unticked the required disclosures. Every flip is reported. The authoritative consent state still rides `co_order_submitted`, which is what a legal question should be answered from.',
        schema: z.object({
            cartId: CART_ID,
            agreed: z.boolean().describe('The state the box arrived at.'),
        }),
    },
    PREORDER_DISCLOSURES_TOGGLED: {
        canonical: 'po_disclosures_toggled',
        surface: SURFACE.Checkout,
        description: 'Preorder buyer ticked or unticked the required disclosures. Every flip is reported. The authoritative consent state still rides `po_order_submitted`, which is what a legal question should be answered from.',
        schema: z.object({
            cartId: CART_ID,
            agreed: z.boolean().describe('The state the box arrived at.'),
        }),
    },
    HOW_IT_WORKS_LINK_CLICKED: {
        canonical: 'co_how_buying_works_opened',
        wireName: 'how_it_works_link_clicked',
        surface: SURFACE.Checkout,
        description: 'Buyer opened the How Buying Works explainer.',
        schema: z.object({
            cartId: CART_ID,
            totalCartCost: z.number().describe('Cart total in USD when the explainer opened.'),
        }),
    },
    /**
     * Checkout's chat click. The buyer-portal `BP_CHAT_BUTTON_CLICKED` entry
     * deliberately shares this `wireName`, splitting the two surfaces' emissions
     * by their distinct canonicals.
     * @dedupe Not deduped.
     */
    CHAT_BUTTON_CLICKED: {
        canonical: 'co_chat_button_clicked',
        wireName: 'chat_button_clicked',
        surface: SURFACE.Checkout,
        description: 'Buyer opened chat in checkout.',
        schema: z.object({
            cartId: CART_ID,
            supportTopicKey: z
                .enum(Object.keys(SUPPORT_TOPICS))
                .optional()
                .describe('Which support topic the buyer opened chat about. Absent when they used the header chat button, which asks for no topic—a real difference in intent, not a missing value.'),
            sessionType: CHECKOUT_SESSION_TYPE,
        }),
    },
    /**
     * Checkout's contact click. The buyer-portal `BP_CONTACT_BUTTON_CLICKED`
     * entry deliberately shares this `wireName`, splitting the two surfaces'
     * emissions by their distinct canonicals.
     * @dedupe Not deduped.
     */
    CONTACT_BUTTON_CLICKED: {
        canonical: 'co_contact_button_clicked',
        wireName: 'contact_button_clicked',
        surface: SURFACE.Checkout,
        description: 'Buyer clicked a contact button in checkout.',
        schema: z.object({
            cartId: CART_ID,
            sessionType: CHECKOUT_SESSION_TYPE,
        }),
    },
    /** @dedupe Not deduped. */
    BACK_TO_OEM_CART: {
        canonical: 'co_back_to_oem_cart_clicked',
        wireName: 'back_to_oem_cart',
        surface: SURFACE.Checkout,
        description: "Buyer left checkout back to the OEM's own cart.",
        schema: z.object({ cartId: CART_ID }),
    },
    /**
     * The credit application's field-level pair.
     *
     * This form earns field-level instrumentation under convention 13: it is the
     * longest in checkout, it sits behind a step most buyers never reach, and its
     * fields are the ones a buyer is most likely to abandon over. One event each
     * with a discriminator rather than one per field, so adding a field costs an
     * enum member and not an entry.
     *
     * **Nothing here may carry a value.** Every field named below is Tier-3 under
     * convention 9—date of birth, SSN, income, employment—so these events
     * carry the field's name and a bounded code and nothing else. In particular
     * they never carry the validator's message, which is copy that can and does
     * interpolate what the buyer typed.
     * @dedupe Once per field per visit.
     */
    CREDIT_APP_FIELD_ERRORED: {
        canonical: 'co_credit_app_field_errored',
        surface: SURFACE.Checkout,
        description: 'A validation error surfaced on a credit-application field the buyer had touched. Once per field per error type per visit.',
        schema: z.object({
            cartId: CART_ID,
            section: CREDIT_APP_SECTION,
            field: z
                .enum(Object.values(CREDIT_APP_FIELD_IDS))
                .describe('Which input failed. The name only—these fields hold regulated data.'),
            errorType: z
                .enum(['required', 'invalid'])
                .describe('Whether the field was left empty or filled in unacceptably. A bounded code, never the rendered message.'),
        }),
    },
    /** @dedupe Once per section per visit. */
    CREDIT_APP_SECTION_COMPLETED: {
        canonical: 'co_credit_app_section_completed',
        surface: SURFACE.Checkout,
        description: 'Every field in one section of the credit application is filled in and valid. Once per section per visit.',
        schema: z.object({
            cartId: CART_ID,
            section: CREDIT_APP_SECTION,
        }),
    },
    /**
     * The promotion events, replacing the three `co_discount_*` entries.
     *
     * Those three only ever saw the code-entry path, which is the least-used way
     * a buyer gets a promotion: most arrive automatically or by picking a
     * campaign offer, and none of that was reported. They also described a
     * discount *code* rather than a promotion, so an auto-applied campaign with
     * no code could not be expressed at all.
     *
     * The set is deliberately wider than a rename. A promotion can be resolved by
     * the server, applied automatically, chosen by the buyer, entered as a code,
     * removed, or stripped at payment time, and those are six different
     * happenings with six different follow-ups.
     */
    /**
     * The opening snapshot of the promotion set, once per visit.
     *
     * A snapshot plus a delta stream, the same shape `co_checkout_started` and
     * the `_selected` events use: this states what the buyer started with, and
     * `co_promotion_applied` / `_removed` / `_overridden` report every change to
     * it. Re-firing on each change instead would report the same transitions the
     * delta events already carry, and would turn a happening into a state read of
     * the applied set.
     * @dedupe Once per cart per tab, so the configurator handoff reload cannot double-report.
     */
    PROMOTIONS_RESOLVED: {
        canonical: 'co_promotions_resolved',
        surface: SURFACE.Checkout,
        description: 'The promotion set checkout resolved for this cart, as first settled. Once per visit; subsequent changes ride the applied, removed, and overridden events.',
        schema: z.object({
            cartId: CART_ID,
            appliedPromotionIds: z
                .array(z.string())
                .describe('The promotions currently applied, after payment-method and dismissal pruning.'),
            eligiblePromotionIds: z
                .array(z.string())
                .describe('Every promotion the buyer could have, including ones not currently applied.'),
            totalSavings: z
                .number()
                .describe('Combined value in USD of the applied promotions at this moment.'),
            awaitingBuyerSelection: z
                .boolean()
                .describe('Whether a campaign is still withholding its offers pending a buyer choice. The applied set is deliberately incomplete while true, which is why this set cannot ride `co_checkout_started`.'),
        }),
    },
    /**
     * Buyer-driven application only. A promotion the server applies on its own
     * arrives in `co_promotions_resolved`'s `appliedPromotionIds` and gets no
     * event here: it would double-report the same fact, and there is no buyer
     * action to attribute. `source` therefore has no `automatic` member—every
     * value on it names something the buyer did.
     * @dedupe Not deduped.
     */
    PROMOTION_APPLIED: {
        canonical: 'co_promotion_applied',
        surface: SURFACE.Checkout,
        description: 'Buyer applied a promotion: a campaign offer they picked, a code they entered, or one they re-added after removing it. Automatic promotions ride `co_promotions_resolved` instead.',
        schema: z.object({
            cartId: CART_ID,
            promotionId: z.string().describe('The promotion applied.'),
            campaignId: z
                .string()
                .optional()
                .describe('The campaign it belongs to. Absent for a standalone promotion.'),
            source: z
                .enum(['campaign_selection', 'code', 'readded'])
                .describe('Which buyer action applied it.'),
            benefitType: z
                .string()
                .optional()
                .describe('What the promotion gives, e.g. a rebate or a financing rate. Absent when the promotion carries none.'),
            computedAmount: z
                .number()
                .optional()
                .describe('Value in USD to this cart. Absent for a benefit with no cash value, such as a promotional APR.'),
        }),
    },
    /** @dedupe Capped at 10 per visit. */
    PROMOTION_CODE_SUBMITTED: {
        canonical: 'co_promotion_code_submitted',
        surface: SURFACE.Checkout,
        description: 'Buyer pressed Apply on the promotion-code field. Reports the attempt; the outcome is `co_promotion_applied` or `co_promotion_code_failed`.',
        schema: z.object({
            cartId: CART_ID,
            attemptNumber: z
                .number()
                .int()
                .describe('Which code attempt this is for the visit, counting from 1 and stopping at 10.'),
        }),
    },
    /**
     * The code itself is not carried. A promotion code is dealer-authored free
     * text typed by the buyer, so a mistyped one is unbounded input, and the
     * codes that matter resolve to a `promotionId` on the success path anyway.
     * @dedupe Not deduped.
     */
    PROMOTION_CODE_FAILED: {
        canonical: 'co_promotion_code_failed',
        wireName: 'discount_failed',
        surface: SURFACE.Checkout,
        description: 'Buyer entered a promotion code and it was rejected.',
        schema: z.object({
            cartId: CART_ID,
            failureReason: z
                .enum(['unrecognized', 'not_stackable', 'ineligible_vehicle', 'lookup_failed'])
                .describe('Why the code did not apply. `unrecognized` is the buyer mistyping or using a dead code; `not_stackable` means it is real but cannot combine with what they already have; `lookup_failed` is Ekho failing, not the buyer.'),
            productId: z.string().describe('The vehicle the code was tried against.'),
        }),
    },
    /** @dedupe Not deduped. */
    PROMOTION_REMOVED: {
        canonical: 'co_promotion_removed',
        surface: SURFACE.Checkout,
        description: 'Buyer removed a promotion they held. Paired with `co_promotion_applied` so the applied set stays derivable across a visit.',
        schema: z.object({
            cartId: CART_ID,
            promotionId: z.string().describe('The promotion removed.'),
            campaignId: z
                .string()
                .optional()
                .describe('The campaign it belonged to. Absent for a standalone promotion.'),
            wholeCampaignRemoved: z
                .boolean()
                .describe('Whether removing this one dropped its whole campaign. Removing a sibling of a buyer-selection campaign clears every member, so one press can retract several promotions.'),
        }),
    },
    /** @dedupe Not deduped. */
    PROMOTION_DETAILS_SHOWN: {
        canonical: 'co_promotion_details_shown',
        surface: SURFACE.Checkout,
        description: 'Buyer opened the terms of a specific promotion via Learn more.',
        schema: z.object({
            cartId: CART_ID,
            promotionId: z.string().describe('The promotion whose terms were opened.'),
            isApplied: z
                .boolean()
                .describe('Whether they already held it, which separates checking terms from shopping.'),
        }),
    },
    /** @dedupe Capped at 5 per visit, keyed on the modal opening. */
    PROMOTION_MANAGER_SHOWN: {
        canonical: 'co_promotion_manager_shown',
        surface: SURFACE.Checkout,
        description: 'Buyer opened the promotions modal.',
        schema: z.object({
            cartId: CART_ID,
            appliedCount: z.number().int().describe('How many promotions were applied when it opened.'),
            eligibleCount: z.number().int().describe('How many were available when it opened.'),
        }),
    },
    /**
     * The buyer losing a promotion through no action of their own.
     *
     * This is the system-change case convention 2 allows an event for: the buyer
     * did not act, and they find out their total went up by reading an error or
     * watching the number move. It is deliberately not reported as
     * `co_promotion_removed`—attributing it to the buyer would misread every
     * removal rate built on that event.
     *
     * `cause` names what took the promotion away, which is a different thing from
     * a property that exists to say why the event fired: both causes are the same
     * happening to the buyer, and the two are worth separating because only one
     * of them is a failure. A payment-method change is the product working as
     * designed; a payment-intent rejection is a promotion that should have been
     * honoured and was not.
     * @dedupe Not deduped.
     */
    PROMOTION_OVERRIDDEN: {
        canonical: 'co_promotion_overridden',
        surface: SURFACE.Checkout,
        description: 'Checkout stripped promotions the buyer was holding, raising their total without them asking.',
        schema: z.object({
            cartId: CART_ID,
            rejectedPromotionIds: z.array(z.string()).describe('The promotions that were taken away.'),
            rejectedCount: z
                .number()
                .int()
                .describe('How many were stripped, so the common aggregation needs no unnesting.'),
            cause: z
                .enum(['payment_intent_rejected', 'payment_method_changed'])
                .describe('What took them away. `payment_intent_rejected` is the server refusing a promotion at payment-intent time; `payment_method_changed` is the buyer switching to a method the promotion does not permit, which silently prunes it.'),
        }),
    },
    /**
     * Address-interaction events for the shared `AddressInput` component. All are
     * CDP-only and all carry `addressField`, because the component renders on three
     * checkout surfaces with identical destinations: only differing destinations
     * justify separate event names, and these have none.
     *
     * None of these carry an address string, any component of one, or a rendered
     * validation message. Addresses are PII and must not reach the CDP legs. Rank,
     * counts, and field names answer the same questions; if address text is ever
     * genuinely required it belongs on the `opts.contactInfo` channel.
     */
    CO_ADDRESS_SUGGESTION_SELECTED: {
        canonical: 'co_address_suggestion_selected',
        surface: SURFACE.Checkout,
        description: 'Buyer picked an address from the autocomplete dropdown.',
        schema: z.object({
            cartId: CART_ID,
            addressField: ADDRESS_FIELD,
            suggestionRank: z
                .number()
                .int()
                .positive()
                .describe('Position of the chosen suggestion in the list, counting from 1.'),
            suggestionCount: z
                .number()
                .int()
                .nonnegative()
                .describe('How many suggestions were offered.'),
            queryLength: z
                .number()
                .int()
                .nonnegative()
                .describe('Characters typed before choosing. Length only—never the query itself.'),
        }),
    },
    CO_ADDRESS_MANUAL_ENTRY_SELECTED: {
        canonical: 'co_address_manual_entry_selected',
        surface: SURFACE.Checkout,
        description: 'Buyer chose to enter their address by hand, from either the dropdown option or the link below the field.',
        schema: z.object({
            cartId: CART_ID,
            addressField: ADDRESS_FIELD,
            source: z.enum(['link', 'dropdown']).describe('Which control took them to manual entry.'),
            queryLength: z
                .number()
                .int()
                .nonnegative()
                .describe('Characters typed before choosing. Length only—never the query itself.'),
            suggestionCount: z
                .number()
                .int()
                .nonnegative()
                .describe('How many suggestions were offered.'),
        }),
    },
    CO_ADDRESS_CLEARED: {
        canonical: 'co_address_cleared',
        surface: SURFACE.Checkout,
        description: 'Buyer cleared the address field.',
        schema: z.object({
            cartId: CART_ID,
            addressField: ADDRESS_FIELD,
            wasComplete: z
                .boolean()
                .describe('Whether the address was valid and whole before being cleared.'),
        }),
    },
    /**
     * Deliberately not deduped: the repeat count is the friction signal. Editing a
     * field, tabbing out, returning, and editing again reports twice. Autofill
     * reports on the change itself rather than on blur, because a browser fill
     * populates several fields at once and may never produce a blur.
     * @dedupe Once per field per visit.
     */
    CO_ADDRESS_FIELD_EDITED: {
        canonical: 'co_address_field_edited',
        surface: SURFACE.Checkout,
        description: 'Buyer edited one of the manual address fields and moved focus away. Reports every edit, not just the first.',
        schema: z.object({
            cartId: CART_ID,
            addressField: ADDRESS_FIELD,
            field: ADDRESS_SUBFIELD.describe('Which input within the manual address form.'),
            becameEmpty: z.boolean().describe('Whether the edit left the input empty.'),
            trigger: z
                .enum(['manual', 'autofill'])
                .describe('Whether the buyer typed it or the browser filled it.'),
            addressOrigin: z
                .enum(['autocomplete', 'manual'])
                .describe('How the address was started: picked from autocomplete, or typed out.'),
        }),
    },
    /**
     * The impression for the address-confirmation popup, so its resolutions have a
     * denominator and a buyer who abandons the popup is visible at all. `reason`
     * separates acquiring a ZIP+4 we never captured from correcting an address the
     * buyer mistyped, which are different problems with different fixes.
     */
    CO_ADDRESS_POPUP_SUGGESTION_SHOWN: {
        canonical: 'co_address_popup_suggestion_shown',
        surface: SURFACE.Checkout,
        description: 'The address-confirmation popup opened.',
        schema: z.object({
            cartId: CART_ID,
            addressField: ADDRESS_FIELD,
            reason: z.enum(['normalization', 'missing_zip4']).describe('Why the suggestion was offered.'),
        }),
    },
    /**
     * `edited` covers a buyer who accepts neither the suggestion nor what they
     * originally typed, which is the strongest signal that both were wrong. Only
     * surfaces whose popup offers an edit path emit it.
     */
    CO_ADDRESS_POPUP_SUGGESTION_CONFIRMED: {
        canonical: 'co_address_popup_suggestion_confirmed',
        surface: SURFACE.Checkout,
        description: 'Buyer resolved the address-confirmation popup, keeping the normalized suggestion, what they entered, or an address they edited in the popup.',
        schema: z.object({
            cartId: CART_ID,
            addressField: ADDRESS_FIELD,
            choice: z
                .enum(['suggested', 'entered', 'edited'])
                .describe('Which version of the address they accepted.'),
        }),
    },
    CO_ADDRESS_POPUP_SUGGESTION_DECLINED: {
        canonical: 'co_address_popup_suggestion_declined',
        surface: SURFACE.Checkout,
        description: 'Buyer dismissed the address-confirmation popup.',
        schema: z.object({
            cartId: CART_ID,
            addressField: ADDRESS_FIELD,
        }),
    },
    /**
     * Deduped per address field, subfield, and error type per visit. Blur-fired
     * validation is noisy, and a buyer tabbing through an empty form would
     * otherwise report every field on every pass.
     *
     * `errorType` separates the ways a field can fail, because they call for
     * different fixes: `required` is an empty field, `zip_extension_required` is a
     * present but 5-digit zip where the surface demands ZIP+4, and `invalid` is a
     * consumer-supplied validation failure.
     * @dedupe Once per field per visit.
     */
    CO_ADDRESS_FIELD_ERRORED: {
        canonical: 'co_address_field_errored',
        surface: SURFACE.Checkout,
        description: 'A validation error surfaced on an address field as the buyer moved focus away.',
        schema: z.object({
            cartId: CART_ID,
            addressField: ADDRESS_FIELD,
            field: ADDRESS_SUBFIELD.describe('Which input within the manual address form.'),
            errorType: z
                .enum(['required', 'zip_extension_required', 'invalid'])
                .describe('The kind of validation failure. A bounded code, never the rendered message.'),
        }),
    },
    /**
     * The prequal counterpart to the registration and shipping validation
     * milestones in `FUNNEL_EVENTS`. It lives here because it is CDP-only: the
     * prequal address feeds no ad destination.
     */
    CO_PREQUAL_ADDRESS_COMPLETED: {
        canonical: 'co_prequal_address_completed',
        surface: SURFACE.Checkout,
        description: 'Buyer finished entering their prequalification address and it is valid. Fires once per distinct valid address, so a genuine change reports again.',
        schema: z.object({ cartId: CART_ID }),
    },
    /** @dedupe Once per unload, and suppressed once the order is placed or the departure was intentional. */
    CHECKOUT_BUYER_ABANDONED_CART: {
        canonical: 'co_cart_abandoned',
        wireName: 'checkout_buyer_abandoned_cart',
        surface: SURFACE.Checkout,
        description: 'Buyer left checkout with work in progress. Never fires for a placed order, or for the buyer moving from the configurator into checkout—that is a step forward, not a departure.',
        schema: z.object({ ...ABANDONED_CART }),
    },
    /** @dedupe Once per unload, and suppressed once the order is placed or the departure was intentional. */
    PREORDER_CART_ABANDONED: {
        canonical: 'po_cart_abandoned',
        surface: SURFACE.Checkout,
        description: 'The `po_` counterpart to `co_cart_abandoned`, for the preorder funnel.',
        schema: z.object({ ...ABANDONED_CART }),
    },
    // -------------------------------------------------------------------------
    // Buyer Portal (post-purchase transaction hub)—Segment/PostHog only
    // -------------------------------------------------------------------------
    BUYER_CLICKED_ORDER_STATUS_MODAL: {
        canonical: 'bp_order_status_modal_opened',
        wireName: 'buyer_clicked_order_status_modal',
        surface: SURFACE.BuyerPortal,
        description: 'Buyer opened an order-status detail modal.',
        schema: z.object({
            purchasedVehicleId: z.string(),
            purchasedVehicleName: z.string().optional(),
            vehicleStage: z.string(),
            orderId: z.string(),
            buyerId: z.string(),
            modalId: z.string(),
            numBoltOnAccessories: z.number(),
            daysSinceDatePurchased: z.number(),
        }),
    },
    BP_VEHICLE_VIEWED: {
        canonical: 'bp_vehicle_viewed',
        surface: SURFACE.BuyerPortal,
        description: "Buyer viewed a purchased vehicle's detail page.",
        schema: z.object({
            purchasedVehicleId: z.string(),
            purchasedVehicleName: z.string().optional(),
            daysSinceDatePurchased: z.number(),
            vehicleStage: z.string(),
            orderId: z.string(),
            financingComplete: z.boolean(),
            registrationAndTitlingComplete: z.boolean(),
            documentsAndSignaturesComplete: z.boolean(),
            deliveryDetailsComplete: z.boolean(),
            paymentComplete: z.boolean(),
            numSectionsCompleted: z.number(),
        }),
    },
    /**
     * Buyer-portal contact click. Deliberately shares its `wireName` with the
     * checkout `CONTACT_BUTTON_CLICKED` entry; the two split by canonical.
     */
    BP_CONTACT_BUTTON_CLICKED: {
        canonical: 'bp_contact_button_clicked',
        wireName: 'contact_button_clicked',
        surface: SURFACE.BuyerPortal,
        description: 'Buyer clicked a contact button in the portal.',
    },
    /**
     * Buyer-portal chat click. Deliberately shares its `wireName` with the
     * checkout `CHAT_BUTTON_CLICKED` entry; the two split by canonical.
     */
    BP_CHAT_BUTTON_CLICKED: {
        canonical: 'bp_chat_button_clicked',
        wireName: 'chat_button_clicked',
        surface: SURFACE.BuyerPortal,
        description: 'Buyer opened chat from the support modal.',
        schema: z.object({
            supportTopicKey: z.string(),
            supportTopic: z.string(),
        }),
    },
    /**
     * The full enriched email payload (support recipient, buyer contact, portal
     * links, email context) rides the event props alongside the template id —
     * the schema pins only the stable field; the rest is free-form.
     */
    BP_SUPPORT_EMAIL_SENT: {
        canonical: 'bp_support_email_sent',
        surface: SURFACE.BuyerPortal,
        description: 'Buyer sent a support email.',
        schema: z.object({
            emailTemplateId: z.string(),
        }),
    },
    // -------------------------------------------------------------------------
    // SDK (embedded widget on dealer sites)
    // -------------------------------------------------------------------------
    PAGE_VIEW: {
        canonical: 'sdk_host_page_viewed',
        wireName: 'page_view',
        surface: SURFACE.Sdk,
        description: 'A page on the host site was viewed.',
        destinations: [EVENT_DESTINATION.GtmDataLayer],
        adEventNames: { gtm: 'page_view' },
        schema: z.object({
            page_title: z.string(),
            page_location: z.string(),
        }),
    },
    /**
     * SDK page view for the Ekho CDP, the same host-page interaction as
     * `sdk_host_page_viewed` on a different rail. Named distinctly so an
     * unprefixed `page_view` can't be ambiguous in the shared PostHog project or
     * collide with a dealer's own events in Segment.
     */
    SDK_PAGE_VIEW: {
        canonical: 'sdk_page_viewed',
        wireName: 'sdk_page_view',
        surface: SURFACE.Sdk,
        description: 'A page on the host site was viewed.',
        // Fully replaced by the page-view beacon, which records this to our own
        // infrastructure at the cost of a log row. Both vendors were billing for a
        // second copy of a number the warehouse already holds: Segment per tracked
        // user, PostHog per event captured.
        //
        // Segment could only stop once nothing read its export. Its two-day
        // parallel run agreed to within 6%, every dealer positive and none
        // negative, and the warehouse model that replaces it now serves every
        // Metabase consumer that used to read the export directly.
        cdp: { posthog: false, segment: false },
        schema: z.object({
            page_title: z.string(),
            page_location: z.string(),
        }),
    },
    SDK_CHAT_OPENED: {
        canonical: 'sdk_chat_opened',
        surface: SURFACE.Sdk,
        description: 'Chat widget was opened on the host page.',
        destinations: [EVENT_DESTINATION.GtmDataLayer],
        adEventNames: { gtm: 'sdk_chat_opened' },
        schema: z.object({
            trigger: z.string(),
        }),
    },
    SDK_PURCHASE_AVAILABILITY_RECEIVED: {
        canonical: 'sdk_purchase_availability_received',
        surface: SURFACE.Sdk,
        description: 'The dealer page asked `Ekho.checkout.isAvailable` whether a vehicle can be bought online, and the answer came back. A request that failed reports `reason: errored` rather than a separate event. Once per vehicle identifier per page URL, with a failure deduped apart from an answer so a retry that succeeds still reports: dealer pages often re-check on re-render, and each repeat would inflate the count. Sent on the SDK event beacon (`/sdk/ev`) into `request_logs`, one query parameter per property, booleans as `1` or `0` and an absent property omitted.',
        // Recorded by the event beacon at the cost of a log row. Dealer pages check
        // availability on nearly every vehicle page, so this fires at page-view
        // scale, and Segment would bill every SDK visitor as a tracked user again.
        cdp: { posthog: false, segment: false },
        schema: z.object({
            available: z.boolean().describe('Whether the vehicle can be bought online.'),
            reason: z
                .enum(Object.values(SDK_AVAILABILITY_REPORTED_REASONS))
                .optional()
                .describe('Why the vehicle is not available, or `errored` when the request failed. Absent when available, and when the BFF declined without a reason.'),
            hasZip: z
                .boolean()
                .describe('Whether the check carried a buyer ZIP, which adds the market-area test.'),
            identifierType: z
                .enum(Object.values(SDK_AVAILABILITY_IDENTIFIER_TYPES))
                .optional()
                .describe('Which vehicle identifier the check used. Absent when the dealer passed neither.'),
        }),
    },
    SDK_BUY_ONLINE_CLICKED: {
        canonical: 'sdk_buy_online_clicked',
        surface: SURFACE.Sdk,
        description: 'The dealer page called `Ekho.checkout.createCart`, which dealers wire to their buy-online button, so this is the hand-off click into checkout. Fires before the request, so a cart that then fails to create still counts. Un-deduped: each press is a real attempt.',
        schema: z.object({
            isPreorder: z.boolean().describe('Whether the cart was created as a preorder.'),
        }),
    },
    // -------------------------------------------------------------------------
    // Sales Agent widget (AISA—embedded chat widget on dealer sites)
    // -------------------------------------------------------------------------
    // The `sa_` prefix scopes widget events alongside `carth_*` / `co_*` in the
    // shared PostHog project. All `sa_*` entries are CDP-only by design.
    SA_WIDGET_LOADED: {
        canonical: 'sa_widget_loaded',
        surface: SURFACE.SalesAgent,
        // PostHog only. The page-view beacon replaces the warehouse copy, and its
        // parallel run agreed to within 7%, so Segment no longer earns the tracked
        // user it bills for every impression.
        //
        // PostHog stays, and this is the one of the three that must. 45 saved
        // insights read this event there, 15 of them funnels using it as the entry
        // step, and a funnel has to join it to the PostHog events that follow.
        // The beacon lands in the warehouse and cannot be joined to anything
        // inside PostHog, so stopping it there would report zero conversions
        // rather than a smaller number.
        cdp: { segment: false },
        description: 'The widget loaded on a dealer page. Fires once per page load, useful as a proxy for host site page hit.',
    },
    SA_WIDGET_EXPANDED: {
        canonical: 'sa_widget_expanded',
        surface: SURFACE.SalesAgent,
        description: 'The chat drawer opened. Filter on source to separate buyer-initiated opens from automatic ones. When a nudge or a dealer-authored action link initiated the open, `nudgeId` or `actionLinkId` names the origin independently of `source`—the widget writes both.',
        schema: z.object({
            messageCount: z.number(),
            source: z.enum([
                'restored',
                'external_event',
                'chat_button',
                'trigger_pill',
                'search_pill',
                'quick_action',
                'user_send',
                'nudge',
                'action_link',
            ]),
            nudgeId: z
                .string()
                .optional()
                .describe('Firestore id of the resolved nudge that initiated the open. Absent when the open was buyer-initiated from any launcher, restored from a prior session, or triggered by an external `ekho:send` event.'),
            actionLinkId: z
                .string()
                .optional()
                .describe('Id of the dealer-authored action link that dispatched this open, from the per-dealer link map on the shell response. Absent when the open did not originate from a `#ekho-<id>` URL fragment—every launcher, nudge, restore, and external-event open.'),
        }),
    },
    SA_WIDGET_COLLAPSED: {
        canonical: 'sa_widget_collapsed',
        surface: SURFACE.SalesAgent,
        description: 'Buyer closed the chat drawer.',
        schema: z.object({
            messageCount: z.number(),
        }),
    },
    SA_NUDGE_SHOWN: {
        canonical: 'sa_nudge_shown',
        surface: SURFACE.SalesAgent,
        // PostHog only. It is the exposure event for the nudge-ordering
        // experiment, and PostHog's own client tags each captured event with the
        // flag variant, which is what allows the analysis to segment by it. That
        // tagging cannot be reproduced anywhere else, so PostHog stays. Segment
        // adds a tracked user per impression for a copy nothing reads: six
        // warehouse queries in thirty days, the last of them weeks ago.
        cdp: { segment: false },
        description: 'A nudge became visible above the collapsed launcher. Dedupe: once per `nudgeId` per session, because a nudge is retired the moment it renders. This is narrower than the vocabulary default for `_shown` and is enforced at the render call site, not at the adapter.',
        schema: z.object({
            nudgeId: z
                .string()
                .describe('Firestore id of the resolved nudge. The reserved id `proactive_bubble_v1` names the pre-engine impressions.'),
            pageType: nudgePageSchema.describe("Page bucket the resolver classified this view under: `vdp` when the buyer's URL matched a known vehicle for the dealer, `general` otherwise. Roughly half of live dealers publish no VDP URLs, and every silent-failure mode of classification degrades to `general`, so a nudge configured for both buckets has its impressions attributable per page type from this dimension alone."),
            assetKind: nudgeAssetKindSchema.describe("Kind of asset the nudge rendered, read straight off the resolved payload's `asset.kind`. Bounded to the closed contract union so segmenting by asset never sees an unexpected value."),
            actionKind: nudgeActionKindSchema.describe("Kind of action the nudge's tap dispatches, read straight off the resolved payload's `action.kind`. Reported on `_shown` so a nudge that never gets clicked still has its intended action type in the impression stream."),
            orderingStrategy: nudgeOrderingStrategySchema.describe("Ordering strategy the resolver actually applied for this page view, read off the resolve response. PostHog separately attaches the flag's own variant to every event as `$feature/sales-agent-nudge-ordering`, so the two disagreeing is the signal an arm is not what the dashboard believes and per-nudge rates segmented on this dimension reflect what the buyer saw, not what the flag intended."),
            impressionOrdinal: z
                .number()
                .int()
                .describe("Zero-based count of nudges the buyer has already been shown this session, so `0` is the session's first nudge. This is the position confound: a session's first nudge is expected to outperform its third regardless of content, so per-nudge rates are only comparable when this dimension is controlled for or randomized across."),
            eligibleCount: z
                .number()
                .int()
                .describe('How many nudges passed the full eligibility predicate for this page view. A value of `1` means the ordering strategy had no choice to make, so those impressions must be excluded when measuring which nudge wins a fair draw—otherwise a nudge that is frequently the only eligible option looks like it is winning draws it never entered.'),
        }),
    },
    /**
     * Proactive nudge bubble tap. `nudgeId` is the origin identity and is carried
     * on both this event and `SA_WIDGET_EXPANDED` independently of `source`.
     * MobileChat's focusin handler can re-fire `openDrawerAtom` with
     * `chat_button` on any refocus of the textarea, and only short-circuits when
     * the drawer is already open (`MobileChat.tsx` handleFocusIn). A nudge-driven
     * open reaches the atom before the focus event, so the emitted `source` is
     * the atom's own value; the design still writes `nudgeId` unconditionally so
     * attribution does not depend on ordering that could change under a future
     * mobile refactor.
     */
    SA_NUDGE_CLICKED: {
        canonical: 'sa_nudge_clicked',
        wireName: 'sa_proactive_bubble_clicked',
        surface: SURFACE.SalesAgent,
        description: 'Buyer tapped the nudge to engage with it. Dedupe: once per `nudgeId` per session, because a nudge is retired the moment it renders, so a second click on the same nudge is not reachable.',
        schema: z.object({
            nudgeId: z
                .string()
                .describe('Firestore id of the resolved nudge. The reserved id `proactive_bubble_v1` names the pre-engine impressions.'),
            pageType: nudgePageSchema.describe("Page bucket the resolver classified this view under: `vdp` when the buyer's URL matched a known vehicle for the dealer, `general` otherwise. Every silent-failure mode of classification degrades to `general`, so a nudge configured for both buckets has its clicks attributable per page type from this dimension alone."),
            assetKind: nudgeAssetKindSchema.describe("Kind of asset the nudge rendered, read straight off the resolved payload's `asset.kind`. Bounded to the closed contract union so segmenting by asset never sees an unexpected value."),
            actionKind: nudgeActionKindSchema.describe("Kind of action this tap dispatched, read straight off the resolved payload's `action.kind`. `prompt` sends a canned message; `form` opens a registered quick-action flow."),
            orderingStrategy: nudgeOrderingStrategySchema.describe("Ordering strategy the resolver actually applied for this page view, read off the resolve response. PostHog separately attaches the flag's own variant to every event as `$feature/sales-agent-nudge-ordering`, so the two disagreeing is the signal an arm is not what the dashboard believes and per-nudge click rates segmented on this dimension reflect what the buyer saw, not what the flag intended."),
            impressionOrdinal: z
                .number()
                .int()
                .describe("Zero-based count of nudges the buyer had already been shown this session at the moment of impression, so `0` is the session's first nudge. This is the position confound: a session's first nudge is expected to outperform its third regardless of content, so per-nudge click rates are only comparable when this dimension is controlled for or randomized across."),
            eligibleCount: z
                .number()
                .int()
                .describe('How many nudges passed the full eligibility predicate for the page view this click originated from. A value of `1` means the ordering strategy had no choice to make, so those impressions must be excluded when measuring which nudge wins a fair draw—otherwise a nudge that is frequently the only eligible option looks like it is winning draws it never entered.'),
        }),
    },
    /**
     * Explicit × dismissal of the proactive nudge bubble—the counter-signal to
     * `SA_NUDGE_CLICKED`. Fired only from the bubble's close button, so
     * it captures active rejection and never the engagement paths that also hide
     * the bubble (e.g. opening the chat).
     */
    SA_NUDGE_DISMISSED: {
        canonical: 'sa_nudge_dismissed',
        wireName: 'sa_proactive_bubble_dismissed',
        surface: SURFACE.SalesAgent,
        description: 'Buyer dismissed the nudge with its close button. Dedupe: once per `nudgeId` per session, because a nudge is retired the moment it renders. This is narrower than the vocabulary default for `_dismissed`, which would otherwise let it recur.',
        schema: z.object({
            nudgeId: z
                .string()
                .describe('Firestore id of the resolved nudge. The reserved id `proactive_bubble_v1` names the pre-engine impressions.'),
            pageType: nudgePageSchema.describe("Page bucket the resolver classified this view under: `vdp` when the buyer's URL matched a known vehicle for the dealer, `general` otherwise. Every silent-failure mode of classification degrades to `general`, so an active rejection carries the same page-type dimension as the impression and click it counters."),
            assetKind: nudgeAssetKindSchema.describe("Kind of asset the nudge rendered, read straight off the resolved payload's `asset.kind`. Bounded to the closed contract union so segmenting by asset never sees an unexpected value."),
            actionKind: nudgeActionKindSchema.describe("Kind of action the nudge's tap would have dispatched, read straight off the resolved payload's `action.kind`. Reported on `_dismissed` so an active rejection carries the same dimensions as the impression and click it counters."),
            orderingStrategy: nudgeOrderingStrategySchema.describe("Ordering strategy the resolver actually applied for this page view, read off the resolve response. PostHog separately attaches the flag's own variant to every event as `$feature/sales-agent-nudge-ordering`, so the two disagreeing is the signal an arm is not what the dashboard believes and per-nudge dismissal rates segmented on this dimension reflect what the buyer saw, not what the flag intended."),
            impressionOrdinal: z
                .number()
                .int()
                .describe("Zero-based count of nudges the buyer had already been shown this session at the moment of impression, so `0` is the session's first nudge. This is the position confound: a session's first nudge is expected to outperform its third regardless of content, so per-nudge dismissal rates are only comparable when this dimension is controlled for or randomized across."),
            eligibleCount: z
                .number()
                .int()
                .describe('How many nudges passed the full eligibility predicate for the page view this dismissal originated from. A value of `1` means the ordering strategy had no choice to make, so those impressions must be excluded when measuring which nudge wins a fair draw—otherwise a nudge that is frequently the only eligible option looks like it is winning draws it never entered.'),
        }),
    },
    SA_MESSAGE_SENT_USER: {
        canonical: 'sa_message_sent_user',
        surface: SURFACE.SalesAgent,
        description: 'Buyer sent a chat message (typed, quick reply, skill pill, retry, or programmatic trigger).',
        schema: z.object({
            // Null on the first message of a new conversation—the server id does
            // not exist yet; join on clientConversationId.
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
            messageIndex: z.number(),
            charLength: z.number(),
            source: z.enum(['typed', 'quick_reply', 'skill_pill', 'trigger']),
        }),
    },
    SA_MESSAGE_RECEIVED_ASSISTANT: {
        canonical: 'sa_message_received_assistant',
        surface: SURFACE.SalesAgent,
        description: 'Assistant reply was delivered.',
        schema: z.object({
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
            messageId: z.string(),
            latencyMs: z.number(),
        }),
    },
    /**
     * The buyer saw an error in place of a reply. Distinguishes real agent
     * failures from benign attrition (buyer left mid-stream) in the
     * sent-vs-received gap.
     */
    SA_MESSAGE_FAILED: {
        canonical: 'sa_message_failed',
        surface: SURFACE.SalesAgent,
        description: 'Assistant failed to deliver a reply and the buyer saw an error.',
        schema: z.object({
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
            // Raw error message—high-cardinality, for debugging only.
            reason: z.string(),
            category: z.enum(['network', 'timeout', 'server_error', 'recaptcha', 'unknown']),
        }),
    },
    SA_TOOL_USE_STARTED: {
        canonical: 'sa_tool_use_started',
        surface: SURFACE.SalesAgent,
        description: 'Assistant started a tool call (e.g. inventory search, appointment slots).',
        schema: z.object({
            toolName: z.string(),
            query: z.string().optional(),
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
        }),
    },
    SA_TOOL_USE_COMPLETED: {
        canonical: 'sa_tool_use_completed',
        surface: SURFACE.SalesAgent,
        description: 'Assistant completed a tool call.',
        schema: z.object({
            toolName: z.string(),
            query: z.string().optional(),
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
            durationMs: z.number().optional(),
        }),
    },
    SA_FEEDBACK_GIVEN: {
        canonical: 'sa_feedback_given',
        surface: SURFACE.SalesAgent,
        description: 'Buyer rated an assistant reply.',
        schema: z.object({
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
            messageId: z.string(),
            rating: z.enum(['up', 'down']),
            // True when a text comment accompanied the rating submission. Comments
            // added after the rating (thumbs-down → popup) are not re-tracked.
            hasComment: z.boolean(),
        }),
    },
    SA_INVENTORY_CARD_CLICKED: {
        canonical: 'sa_inventory_card_clicked',
        surface: SURFACE.SalesAgent,
        description: 'Buyer interacted with an inventory card in chat.',
        schema: z.object({
            vehicleId: z.string(),
            make: z.string().optional(),
            model: z.string().optional(),
            year: z.number().optional(),
            action: z.enum(['tell_me_more', 'open_listing', 'title', 'image']),
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
        }),
    },
    SA_PREQUAL_FORM_SHOWN: {
        canonical: 'sa_prequal_form_shown',
        surface: SURFACE.SalesAgent,
        description: 'Prequal form was shown to the buyer (impression on push).',
        schema: z.object({
            // Absent when the form opens without an originating CTA card (e.g.
            // agent-emitted auto-push before a message is committed).
            messageId: z.string().optional(),
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
        }),
    },
    SA_PREQUAL_FORM_SUBMITTED: {
        canonical: 'sa_prequal_form_submitted',
        surface: SURFACE.SalesAgent,
        description: 'Buyer submitted the prequal form. The phone-keyed identify fires at this step, merging the anonymous session onto the buyer.',
        schema: z.object({
            // Absent when the modal opens without an originating CTA card.
            messageId: z.string().optional(),
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
        }),
    },
    SA_PREQUAL_FORM_RESULT: {
        canonical: 'sa_prequal_form_result',
        surface: SURFACE.SalesAgent,
        description: 'Buyer received a prequal decision.',
        schema: z.object({
            outcome: z.string(),
            messageId: z.string().optional(),
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
        }),
    },
    SA_SMS_HANDOFF_CTA_SHOWN: {
        canonical: 'sa_sms_handoff_cta_shown',
        wireName: 'sa_sms_handoff_started',
        surface: SURFACE.SalesAgent,
        description: 'SMS-handoff card was shown to the buyer (impression, not an action).',
        schema: z.object({
            messageId: z.string(),
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
        }),
    },
    SA_SMS_HANDOFF_PHONE_SUBMITTED: {
        canonical: 'sa_sms_handoff_phone_submitted',
        surface: SURFACE.SalesAgent,
        description: 'Buyer submitted a phone number for SMS handoff.',
        schema: z.object({
            conversationId: z.string(),
            clientConversationId: z.string(),
            messageId: z.string(),
        }),
    },
    SA_SMS_HANDOFF_CONFIRMED: {
        canonical: 'sa_sms_handoff_confirmed',
        surface: SURFACE.SalesAgent,
        description: 'SMS handoff was confirmed.',
        schema: z.object({
            conversationId: z.string(),
            clientConversationId: z.string(),
        }),
    },
    SA_SMS_HANDOFF_FAILED: {
        canonical: 'sa_sms_handoff_failed',
        surface: SURFACE.SalesAgent,
        description: 'SMS handoff failed and the buyer saw an error.',
        schema: z.object({
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
            // Raw error message—high-cardinality, for debugging only.
            reason: z.string(),
            category: z.enum(['network', 'timeout', 'server_error', 'recaptcha', 'unknown']),
        }),
    },
    /**
     * Phone captured via the consent-only flow (TcpaConsentCard in
     * `mode='tcpa-consent'`). Distinct from `sa_sms_handoff_phone_submitted`—no
     * SMS is sent; the buyer is granting TCPA consent. Together the two events
     * cover every in-widget phone collection surface.
     */
    SA_TCPA_CONSENT_SUBMITTED: {
        canonical: 'sa_tcpa_consent_submitted',
        surface: SURFACE.SalesAgent,
        description: 'Buyer granted TCPA consent to be contacted—lead capture without starting an SMS thread.',
        schema: z.object({
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
            messageId: z.string(),
        }),
    },
    SA_BOOK_APPOINTMENT_FORM_SHOWN: {
        canonical: 'sa_book_appointment_form_shown',
        surface: SURFACE.SalesAgent,
        description: 'Booking form was shown to the buyer (impression on push).',
        schema: z.object({
            messageId: z.string(),
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
            bookingType: z.enum(['SALES_CONSULTATION', 'SHOWROOM_VISIT']),
        }),
    },
    SA_BOOK_APPOINTMENT_SUBMITTED: {
        canonical: 'sa_book_appointment_submitted',
        surface: SURFACE.SalesAgent,
        description: 'Buyer submitted the booking form with a chosen slot.',
        schema: z.object({
            messageId: z.string(),
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
            bookingType: z.enum(['SALES_CONSULTATION', 'SHOWROOM_VISIT']),
        }),
    },
    SA_BOOK_APPOINTMENT_CONFIRMED: {
        canonical: 'sa_book_appointment_confirmed',
        surface: SURFACE.SalesAgent,
        description: 'Booking was confirmed by the backend and the completed card was shown.',
        schema: z.object({
            messageId: z.string(),
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
            bookingType: z.enum(['SALES_CONSULTATION', 'SHOWROOM_VISIT']),
        }),
    },
    SA_BOOK_APPOINTMENT_FAILED: {
        canonical: 'sa_book_appointment_failed',
        surface: SURFACE.SalesAgent,
        description: 'Booking failed and the buyer saw an inline error.',
        schema: z.object({
            messageId: z.string(),
            conversationId: z.string().nullable(),
            clientConversationId: z.string().nullable(),
            bookingType: z.enum(['SALES_CONSULTATION', 'SHOWROOM_VISIT']),
            reason: z.string(),
            category: z.enum([
                'network',
                'timeout',
                'server_error',
                'validation',
                'slot_taken',
                'unknown',
            ]),
        }),
    },
    SA_CHAT_MENU_OPENED: {
        canonical: 'sa_chat_menu_opened',
        wireName: 'sa_history_opened',
        surface: SURFACE.SalesAgent,
        description: 'Buyer opened the chat menu (New chat + Chat history). Loading a past conversation is sa_conversation_loaded.',
    },
    SA_CONVERSATION_LOADED: {
        canonical: 'sa_conversation_loaded',
        surface: SURFACE.SalesAgent,
        description: 'A past conversation was loaded—by the buyer from the history menu, or automatically when returning via a listing link.',
        schema: z.object({
            // Both carry the locally-stored conversation id on this event.
            conversationId: z.string(),
            clientConversationId: z.string(),
            messageCount: z.number(),
            source: z.enum(['history_menu', 'query_param_resume']),
        }),
    },
    SA_HOST_PAGE_NAVIGATED: {
        canonical: 'sa_host_page_navigated',
        surface: SURFACE.SalesAgent,
        description: 'Host site navigated to a new page without a full reload (SPA navigation).',
    },
    // -------------------------------------------------------------------------
    // Admin Portal (internal operations console)—CDP-only
    // -------------------------------------------------------------------------
    // Engagement/tool-usage signals the backend can't see: which records an
    // internal user drills into (reads) and how they work the list tools.
    // Object-mutation actions (notes, VIN, model-year, stipulations, provider
    // CRUD, fulfillment) already fire server-side in OPERATIONAL_EVENTS, so
    // there are deliberately no `ap_` copies of those. Singular entity for
    // single-record views, plural for list/collection actions.
    AP_LEAD_VIEWED: {
        canonical: 'ap_lead_viewed',
        surface: SURFACE.AdminPortal,
        description: 'An internal user viewed a lead.',
        schema: z.object({
            leadId: z.string(),
        }),
    },
    AP_ORDER_VIEWED: {
        canonical: 'ap_order_viewed',
        surface: SURFACE.AdminPortal,
        description: 'An internal user viewed an order.',
        schema: z.object({
            orderId: z.string(),
        }),
    },
    AP_PREORDER_VIEWED: {
        canonical: 'ap_preorder_viewed',
        surface: SURFACE.AdminPortal,
        description: 'An internal user viewed a preorder.',
        schema: z.object({
            preorderId: z.string(),
        }),
    },
    AP_CONVERSATION_VIEWED: {
        canonical: 'ap_conversation_viewed',
        surface: SURFACE.AdminPortal,
        description: 'An internal user viewed a conversation thread.',
        schema: z.object({
            conversationId: z.string(),
        }),
    },
    AP_LEADS_FILTERED: {
        canonical: 'ap_leads_filtered',
        surface: SURFACE.AdminPortal,
        description: 'An internal user filtered the leads list.',
        schema: z.object({
            filterKeys: z.array(z.string()),
        }),
    },
    AP_LEADS_SEARCHED: {
        canonical: 'ap_leads_searched',
        surface: SURFACE.AdminPortal,
        description: 'An internal user searched the leads list.',
    },
    AP_ORDERS_FILTERED: {
        canonical: 'ap_orders_filtered',
        surface: SURFACE.AdminPortal,
        description: 'An internal user filtered the orders list.',
        schema: z.object({
            filterKeys: z.array(z.string()),
        }),
    },
    AP_ORDERS_SEARCHED: {
        canonical: 'ap_orders_searched',
        surface: SURFACE.AdminPortal,
        description: 'An internal user searched the orders list.',
    },
    AP_PREORDERS_FILTERED: {
        canonical: 'ap_preorders_filtered',
        surface: SURFACE.AdminPortal,
        description: 'An internal user filtered the preorders list.',
        schema: z.object({
            filterKeys: z.array(z.string()),
        }),
    },
    AP_PREORDERS_SEARCHED: {
        canonical: 'ap_preorders_searched',
        surface: SURFACE.AdminPortal,
        description: 'An internal user searched the preorders list.',
    },
    AP_INVENTORY_FILTERED: {
        canonical: 'ap_inventory_filtered',
        surface: SURFACE.AdminPortal,
        description: 'An internal user filtered the inventory list.',
        schema: z.object({
            filterKeys: z.array(z.string()),
        }),
    },
    AP_INVENTORY_SEARCHED: {
        canonical: 'ap_inventory_searched',
        surface: SURFACE.AdminPortal,
        description: 'An internal user searched the inventory list.',
    },
    AP_CONVERSATIONS_FILTERED: {
        canonical: 'ap_conversations_filtered',
        surface: SURFACE.AdminPortal,
        description: 'An internal user filtered the Inbox.',
        schema: z.object({
            filterKeys: z.array(z.string()),
        }),
    },
    AP_CONVERSATIONS_SEARCHED: {
        canonical: 'ap_conversations_searched',
        surface: SURFACE.AdminPortal,
        description: 'An internal user searched the Inbox.',
    },
    AP_REPORT_EXPORTED: {
        canonical: 'ap_report_exported',
        surface: SURFACE.AdminPortal,
        description: 'An internal user exported a report.',
        schema: z.object({
            reportType: z.string(),
            format: z.string(),
        }),
    },
};

export { ENGAGEMENT_EVENTS };
//# sourceMappingURL=engagement.js.map
